import { useState } from 'react';
import { Link } from 'react-router-dom';

interface AdminSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  pendingCount: number;
  userName: string;
  userEmail: string;
  onChangeEmail: () => void;
  onChangePassword: () => void;
  onLogout: () => void;
}

const navItems = [
  { id: 'dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-3-line' },
  { id: 'membres', label: 'Membres', icon: 'ri-team-line' },
  { id: 'cotisations', label: 'Gérer cotisation', icon: 'ri-wallet-3-line' },
  { id: 'directions', label: 'Directions', icon: 'ri-building-line' },
  { id: 'bureau', label: 'Bureau', icon: 'ri-community-line' },
  { id: 'equipes', label: 'Équipes & Rencontres', icon: 'ri-shield-star-line' },
  { id: 'lives', label: 'Lives', icon: 'ri-live-line' },
  { id: 'actualites', label: 'Actualités', icon: 'ri-newspaper-line' },
  { id: 'loisirs', label: 'Loisirs', icon: 'ri-movie-2-line' },
  { id: 'galerie', label: 'Galerie', icon: 'ri-image-line' },
  { id: 'club', label: 'Trophées & Palmarès', icon: 'ri-trophy-line' },
  { id: 'boutique', label: 'Boutique', icon: 'ri-store-2-line' },
  { id: 'evenements', label: 'Événements', icon: 'ri-calendar-event-line' },
  { id: 'commandes', label: 'Commandes', icon: 'ri-shopping-cart-2-line' },
];

export default function AdminSidebar({ activeTab, onTabChange, pendingCount, userName, userEmail, onChangeEmail, onChangePassword, onLogout }: AdminSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleTabChange = (tab: string) => {
    onTabChange(tab);
    setMobileOpen(false);
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="px-6 py-6 border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://public.readdy.ai/ai/img_res/71bd3ce9-ea40-4eb5-8ec2-3e5ee5114aee.png"
            alt="ASC Sonatel"
            className="w-10 h-10 object-contain"
          />
          <div>
            <div className="text-white font-bold text-sm leading-tight">ASC Sonatel</div>
            <div className="text-[#E8521A] text-xs font-medium">Administration</div>
          </div>
        </Link>
      </div>

      {/* Admin Info */}
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#E8521A] flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-user-line text-white text-base"></i>
          </div>
          <div>
            <div className="text-white text-sm font-semibold">{userName}</div>
            <div className="text-gray-400 text-xs">{userEmail}</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          const showBadge = item.id === 'membres' && pendingCount > 0;
          return (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                isActive
                  ? 'bg-[#E8521A] text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </span>
              <span className="flex-1 text-left whitespace-nowrap">{item.label}</span>
              {showBadge && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <button
          onClick={() => {
            onChangeEmail();
            setMobileOpen(false);
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200 cursor-pointer"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-mail-settings-line text-base"></i>
          </span>
          <span className="whitespace-nowrap">Email de connexion</span>
        </button>
        <button
          onClick={() => {
            onChangePassword();
            setMobileOpen(false);
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200 cursor-pointer"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-lock-password-line text-base"></i>
          </span>
          <span className="whitespace-nowrap">Mot de passe</span>
        </button>
        <Link
          to="/"
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-left-line text-base"></i>
          </span>
          <span className="whitespace-nowrap">Retour au site</span>
        </Link>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 cursor-pointer"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-line text-base"></i>
          </span>
          <span className="whitespace-nowrap">Déconnexion</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex h-screen w-64 flex-shrink-0 flex-col overflow-y-auto overscroll-contain bg-[#1A1A1A]">
        <SidebarContent />
      </aside>

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#1A1A1A] flex items-center justify-between px-4 py-3 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="https://public.readdy.ai/ai/img_res/71bd3ce9-ea40-4eb5-8ec2-3e5ee5114aee.png"
            alt="ASC Sonatel"
            className="w-8 h-8 object-contain"
          />
          <div>
            <div className="text-white font-bold text-xs leading-tight">ASC Sonatel</div>
            <div className="text-[#E8521A] text-xs">Administration</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 text-white cursor-pointer"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-72 bg-[#1A1A1A] z-40 flex flex-col overflow-y-auto overscroll-contain transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>
    </>
  );
}
